import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CsvReaderService {
  importFromCsv(file: File): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const reader: FileReader = new FileReader();
      reader.onload = (): void => {
        observer.next(this.decode(reader.result as ArrayBuffer));
        observer.complete();
      };

      reader.onerror = (error): void => {
        observer.error(error);
      };

      reader.readAsArrayBuffer(file);
      return (): void => reader.abort();
    });
  }

  /**
   * Excel saves "CSV" in the Windows ANSI code page rather than UTF-8, so bytes that are not valid UTF-8
   * are read as Windows-1252 instead of turning every accented letter into a replacement character.
   * A UTF-16 byte order mark still wins, as it did when the file was read with readAsText.
   */
  decode(bytes: ArrayBuffer): string {
    const utf16 = this.utf16Encoding(new Uint8Array(bytes));
    if (utf16) {
      return new TextDecoder(utf16).decode(bytes);
    }
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      return new TextDecoder('windows-1252').decode(bytes);
    }
  }

  private utf16Encoding([first, second]: Uint8Array): string | null {
    if (first === 0xff && second === 0xfe) {
      return 'utf-16le';
    }
    if (first === 0xfe && second === 0xff) {
      return 'utf-16be';
    }
    return null;
  }
}
